import React, { useState, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { API_BASE } from '../lib/rpc';
import { crmFetch } from '../lib/crmRpc';
import { reportDataFetchError } from '../lib/telemetry';
import { useToast } from './ui/ToastProvider';
import { useDialog } from './ui/DialogProvider';
import { generatePassphrase } from '../lib/passphrase';

const isPasswordStrong = (pw: string) => pw.length >= 12 && /[A-Za-z]/.test(pw) && /[0-9]/.test(pw);

const TooltipIcon = ({ text, isLight }: { text: string; isLight: boolean }) => (
  <span className="custom-tooltip-container" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '6px', cursor: 'help', verticalAlign: 'text-bottom' }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#9ca3af' : '#71717a'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
    <div className="custom-tooltip-text" style={{ 
      position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px',
      background: isLight ? '#1f2937' : '#000000', color: '#f9fafb', padding: '0.5rem 0.75rem', borderRadius: '6px',
      fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'normal', width: 'max-content', maxWidth: '280px',
      textAlign: 'center', zIndex: 100, opacity: 0, visibility: 'hidden', transition: 'opacity 0.2s, visibility 0.2s',
      pointerEvents: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }}>
      {text}
      <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderWidth: '4px', borderStyle: 'solid', borderColor: `${isLight ? '#1f2937' : '#000000'} transparent transparent transparent` }}></div>
    </div>
    <style dangerouslySetInnerHTML={{__html: `
      .custom-tooltip-container:hover .custom-tooltip-text { opacity: 1 !important; visibility: visible !important; }
    `}} />
  </span>
);


function KeyValueBuilder({ items, onChange, isLight, title, isSecure }: any) {
  return (
    <div style={{ marginTop: '1rem', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', borderRadius: '8px', overflow: 'visible' }}>
      <div style={{ padding: '0.75rem', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', fontWeight: 600, fontSize: '0.9rem', color: isLight ? '#09090b' : 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
        {title}
      </div>
      <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {items.map((item: any, i: number) => {
          const isPassword = item.secure && item.key.toLowerCase() === 'password';
          const isBirthday = item.secure && item.key.toLowerCase() === 'birthday';
          const isProtected = isPassword || isBirthday;
          return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input 
                type="text" 
                placeholder="Label (e.g. Account Number)" 
                value={item.key} 
                onChange={e => { const newItems = [...items]; newItems[i].key = e.target.value; onChange(newItems); }} 
                readOnly={isProtected}
                style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', background: isProtected ? (isLight ? '#f3f4f6' : 'rgba(0,0,0,0.4)') : (isLight ? '#fff' : 'rgba(0,0,0,0.2)'), color: isProtected ? (isLight ? '#9ca3af' : '#6b7280') : (isLight ? '#09090b' : 'white'), fontSize: '0.85rem' }} 
              />
              <input 
                type={isBirthday ? 'date' : (item.secure && !item.unmasked ? 'password' : 'text')} 
                placeholder="Value" 
                value={item.value} 
                onChange={e => { const newItems = [...items]; newItems[i].value = e.target.value; onChange(newItems); }} 
                style={{ flex: 2, padding: '0.5rem', borderRadius: '4px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#09090b' : 'white', fontSize: '0.85rem', colorScheme: isLight ? 'light' : 'dark' }} 
              />
              {item.secure && !isBirthday && (
                <button onClick={() => { const newItems = [...items]; newItems[i].unmasked = !newItems[i].unmasked; onChange(newItems); }} style={{ padding: '0.5rem', background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={item.unmasked ? "Hide Value" : "Show Value"}>
                  {item.unmasked ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  )}
                </button>
              )}
              {isPassword && (
                <button onClick={() => {
                    const newItems = [...items];
                    newItems[i].value = generatePassphrase();
                    newItems[i].unmasked = true;
                    onChange(newItems);
                }} style={{ padding: '0.5rem', background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Generate Secure Passphrase">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 4V2"/><path d="M15 16v-2"/><path d="M8 9h2"/><path d="M20 9h2"/><path d="M17.8 11.8 19 13"/><path d="M15 9h0"/><path d="M17.8 6.2 19 5"/><path d="M3 21l9-9"/><path d="M12.2 6.2 11 5"/></svg>
                </button>
              )}
              {!isProtected ? (
                <button onClick={() => { const newItems = items.filter((_: any, idx: number) => idx !== i); onChange(newItems); }} style={{ padding: '0.5rem', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>✕</button>
              ) : null}
            </div>
            {isPassword && !isPasswordStrong(item.value) && (
              <div style={{ fontSize: '0.75rem', color: '#ef4444', marginLeft: '2px' }}>
                Hint: Passwords must be at least 12 characters and contain both letters and numbers.
              </div>
            )}
          </div>
        )})}
        <button onClick={() => onChange([...items, { key: '', value: '', secure: !!isSecure }])} style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500, padding: 0 }}>+ Add Field</button>
      </div>
    </div>
  );
}

export function ContactEditorModal({ open, contactId: initialContactId, initialPhoneNumber, defaultData, onClose, isLight, slug }: any) {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm, prompt } = useDialog();
  
  const [contactId, setContactId] = useState<string | null>(initialContactId || null);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [secureNotes, setSecureNotes] = useState('');
  const [numbers, setNumbers] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [secureFields, setSecureFields] = useState<any[]>([]);
  const [keyFingerprint, setKeyFingerprint] = useState<string | null>(null);
  const [keyUpdatedAt, setKeyUpdatedAt] = useState<number | null>(null);
  const [contactList, setContactList] = useState<any[]>([]);
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredContacts = useMemo(() => {
    if (!contactList) return [];
    if (!searchQuery) return contactList.slice(0, 50);
    
    const q = searchQuery.toLowerCase();
    const numQ = q.replace(/\D/g, '');
    
    const results = [];
    for (let i = 0; i < contactList.length; i++) {
       const c = contactList[i];
       const match = c.name?.toLowerCase().includes(q) || 
                     (c.company && c.company.toLowerCase().includes(q)) ||
                     (c.email && c.email.toLowerCase().includes(q)) ||
                     (c.numbers && c.numbers.some((n: any) => n.number && (n.number.includes(q) || (numQ.length > 2 && n.number.includes(numQ)))));
       if (match) {
           results.push(c);
           if (results.length >= 50) break;
       }
    }
    return results;
  }, [contactList, searchQuery]);
  
  const [purgeSecureData, setPurgeSecureData] = useState(false);
  const [regenerateClientKey, setRegenerateClientKey] = useState(false);

  useEffect(() => {
    if (!open) return;
    
    setContactId(initialContactId || null);
    
    if (!initialContactId) {
       setLoading(true);
       const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
       crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book`, {
          headers: { 'x-crm-password': pw }
       })
       .then(res => res.json())
       .then(json => {
          if (json.data) {
             setContactList(json.data);
             if (initialPhoneNumber) {
                 const existing = json.data.find((c: any) => c.numbers?.some((n: any) => n.number === initialPhoneNumber || n.number.includes(initialPhoneNumber)));
                 if (existing) {
                    setContactId(existing.id);
                    populateFromData(existing);
                    setLoading(false);
                    return;
                 }
             }
          }
          if (initialPhoneNumber) {
              populateFromData({ ...(defaultData || {}), numbers: [{ number: initialPhoneNumber, isPrimary: true, canSms: true }] });
          } else {
              populateFromData(defaultData);
          }
          setLoading(false);
       })
       .catch(() => {
          if (initialPhoneNumber) {
              populateFromData({ ...(defaultData || {}), numbers: [{ number: initialPhoneNumber, isPrimary: true, canSms: true }] });
          } else {
              populateFromData(defaultData);
          }
          setLoading(false);
       });
       return;
    }

    if (defaultData || initialContactId) {
      if (initialContactId && !defaultData) {
         // Should fetch by ID but for now we rely on defaultData being passed from manager
      }
      populateFromData(defaultData);
    } else {
      populateFromData(null);
    }
  }, [open, defaultData, initialContactId, initialPhoneNumber, slug]);

  const populateFromData = (data: any) => {
    if (data) {
      setName(data.name || '');
      setCompany(data.company || '');
      setEmail(data.email || '');
      setNumbers(data.numbers ? [...data.numbers] : [{ number: '', isPrimary: true, canSms: true }]);
      
      const cf = [];
      if (data.customFields) {
        for (const [k, v] of Object.entries(data.customFields)) cf.push({ key: k, value: v, secure: false });
      }
      setCustomFields(cf);

      const sf = [];
      let hasPassword = false;
      let hasBirthday = false;
      let loadedSecureNotes = '';
      if (data.secureCustomFields) {
        for (const [k, v] of Object.entries(data.secureCustomFields)) {
            if (k.toLowerCase() === 'notes') {
                loadedSecureNotes = v as string;
            } else {
                sf.push({ key: k, value: v, secure: true, unmasked: false });
                if (k.toLowerCase() === 'password') hasPassword = true;
                if (k.toLowerCase() === 'birthday') hasBirthday = true;
            }
        }
      }
      if (!hasPassword) {
          sf.push({ key: 'Password', value: generatePassphrase(), secure: true, unmasked: false });
      }
      if (!hasBirthday) {
          sf.push({ key: 'Birthday', value: '', secure: true, unmasked: false });
      }
      if (!loadedSecureNotes && data.notes) {
          loadedSecureNotes = data.notes; // MIGRATION!
      }
      setSecureNotes(loadedSecureNotes);
      setSecureFields(sf);
      setKeyFingerprint(data.keyFingerprint || null);
      setKeyUpdatedAt(data.updatedAt || null);
      setPurgeSecureData(false);
      setRegenerateClientKey(false);
    } else {
      setName(''); setCompany(''); setEmail('');
      setSecureNotes('');
      setNumbers([{ number: '', isPrimary: true, canSms: true }]);
      setCustomFields([]); 
      setSecureFields([
          { key: 'Password', value: generatePassphrase(), secure: true, unmasked: false },
          { key: 'Birthday', value: '', secure: true, unmasked: false }
      ]);
      setKeyFingerprint(null); setKeyUpdatedAt(null); setPurgeSecureData(false); setRegenerateClientKey(false);
    }
  };

  if (!open) return null;

  const handleSave = async (overrideSecureFields?: any[], overrideRegenerate?: boolean) => {
    if (email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    setLoading(true);
    try {
      const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
      const finalSecureFields = overrideSecureFields || secureFields;
      
      const filteredNumbers = numbers.filter(n => n.number.trim() !== '').map(n => ({
          ...n,
          number: n.number.replace(/\D/g, '').slice(-10)
      }));
      
      if (filteredNumbers.length === 0) {
          showToast('At least one phone number is required.', 'error');
          setLoading(false);
          return;
      }
      
      const payload: any = {
        id: contactId,
        name,
        company,
        email,
        notes: "", // Send blank to overwrite unencrypted notes in DB
        numbers: filteredNumbers,
        customFields: customFields.reduce((acc, curr) => { if (curr.key) acc[curr.key] = curr.value; return acc; }, {} as any),
        secureCustomFields: {
          ...finalSecureFields.reduce((acc, curr) => { if (curr.key) acc[curr.key] = curr.value; return acc; }, {} as any),
          Notes: secureNotes
        },
        purgeSecureData,
        regenerateClientKey: overrideRegenerate ?? regenerateClientKey
      };

      const res = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
         body: JSON.stringify(payload)
      });
      if (res.ok) {
         onClose(true);
      } else {
         const err = await res.json();
         showToast('Failed to save: ' + (err.error || 'Unknown error'), 'error');
      }
    } catch (e) {
      showToast('Error saving contact.', 'error');
    }
    setLoading(false);
  };

  return (
    <div onClick={() => onClose(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '1rem' }}>
       <div onClick={e => e.stopPropagation()} style={{ background: isLight ? '#ffffff' : '#18181b', borderRadius: '12px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
          <div style={{ padding: '1.5rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)' }}>
            <h2 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1.25rem' }}>{contactId ? 'Edit Contact' : 'New Contact'}</h2>
            <button onClick={() => onClose(false)} style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', padding: '0.5rem' }}>✕</button>
          </div>
          
          <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!contactId && !isMergeMode && (
                <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '-0.5rem' }}>
                   <span onClick={() => setIsMergeMode(true)} style={{ color: '#38bdf8', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'underline' }}>...or add to existing</span>
                </div>
            )}
            
            {isMergeMode ? (
               <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1rem' }}>Merge into Existing Contact</h3>
                      <button onClick={() => setIsMergeMode(false)} style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>Cancel</button>
                  </div>
                  <input type="text" placeholder="Search contacts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.3)', color: isLight ? '#09090b' : 'white', boxSizing: 'border-box' }} />
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', borderRadius: '8px' }}>
                     {filteredContacts.map(c => (
                         <div key={c.id} onClick={() => {
                             setContactId(c.id);
                             const appendedNumbers = [...(c.numbers || [])];
                             if (initialPhoneNumber && !appendedNumbers.some(n => n.number === initialPhoneNumber || n.number.includes(initialPhoneNumber))) {
                                 appendedNumbers.push({ number: initialPhoneNumber, isPrimary: false, canSms: true });
                             }
                             populateFromData({ ...c, numbers: appendedNumbers });
                             setIsMergeMode(false);
                         }} style={{ padding: '0.75rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', cursor: 'pointer', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#09090b' : 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                             <div style={{ display: 'flex', flexDirection: 'column' }}>
                                 <span style={{ fontWeight: 600 }}>{c.name}</span>
                                 {c.company && <span style={{ fontSize: '0.75rem', color: isLight ? '#9ca3af' : '#71717a' }}>{c.company}</span>}
                             </div>
                             <div style={{ fontSize: '0.8rem', color: isLight ? '#6b7280' : '#a1a1aa', textAlign: 'right' }}>
                                 {(() => {
                                     const q = searchQuery.toLowerCase();
                                     const numQ = q.replace(/\D/g, '');
                                     if (q && c.email?.toLowerCase().includes(q)) return c.email;
                                     if (q && c.numbers?.some((n: any) => n.number && (n.number.includes(q) || (numQ.length > 2 && n.number.includes(numQ))))) {
                                         const match = c.numbers.find((n: any) => n.number && (n.number.includes(q) || (numQ.length > 2 && n.number.includes(numQ))));
                                         return match?.number;
                                     }
                                     return null;
                                 })()}
                             </div>
                         </div>
                     ))}
                     {filteredContacts.length === 0 && <div style={{ padding: '1rem', textAlign: 'center', color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.9rem' }}>{contactList.length === 0 ? 'No contacts available.' : 'No matches found.'}</div>}
                  </div>
               </div>
            ) : (
                <>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.85rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Name *</span>
                  <span style={{ fontSize: '0.75rem', color: isLight ? '#9ca3af' : '#71717a', fontStyle: 'italic' }}>Displays on Caller ID</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.3)', color: isLight ? '#09090b' : 'white', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Company</label>
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.3)', color: isLight ? '#09090b' : 'white', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.3)', color: isLight ? '#09090b' : 'white', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginTop: '0.5rem', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', borderRadius: '8px', overflow: 'hidden' }}>
               <div style={{ padding: '0.75rem', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', fontWeight: 600, fontSize: '0.9rem', color: isLight ? '#09090b' : 'white' }}>Phone Numbers</div>
               <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {numbers.map((num, i) => (
                    <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input type="text" placeholder="10-digit number" value={num.number} onChange={e => { const n = [...numbers]; n[i].number = e.target.value; setNumbers(n); }} style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', background: isLight ? '#fff' : 'rgba(0,0,0,0.2)', color: isLight ? '#09090b' : 'white', fontSize: '0.85rem' }} />
                      <label style={{ fontSize: '0.8rem', color: isLight ? '#4b5563' : '#a1a1aa', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input type="checkbox" checked={num.isPrimary} onChange={e => { const n = [...numbers]; if (e.target.checked) n.forEach(x => x.isPrimary = false); n[i].isPrimary = e.target.checked; setNumbers(n); }} /> Primary
                      </label>
                      <label style={{ fontSize: '0.8rem', color: isLight ? '#4b5563' : '#a1a1aa', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input type="checkbox" checked={num.canSms} onChange={e => { const n = [...numbers]; n[i].canSms = e.target.checked; setNumbers(n); }} /> SMS
                      </label>
                      <button onClick={() => setNumbers(numbers.filter((_, idx) => idx !== i))} style={{ padding: '0.5rem', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setNumbers([...numbers, { number: '', isPrimary: numbers.length === 0, canSms: true }])} style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500, padding: 0 }}>+ Add Number</button>
               </div>
            </div>

            <KeyValueBuilder title={<div style={{ display: 'flex', alignItems: 'center' }}>Custom Fields <TooltipIcon text="These fields are stored in plain text and remain visible even after a Crypto-Shred. Do not store sensitive, medical (HIPAA), or financial (PCI) data here." isLight={isLight} /></div>} isSecure={false} items={customFields} onChange={setCustomFields} isLight={isLight} />
            <KeyValueBuilder title={<div style={{ display: 'flex', alignItems: 'center' }}>Secure Fields (PCI/HIPAA) <TooltipIcon text="Fully encrypted and HIPAA-compliant. To maintain PCI-DSS compliance, do not store credit card CVV codes here. For payments, use a secure processor and store only the token or last 4 digits." isLight={isLight} /></div>} isSecure={true} items={secureFields} onChange={setSecureFields} isLight={isLight} />

            <div style={{ paddingBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', marginBottom: '0.25rem', color: isLight ? '#4b5563' : '#a1a1aa', marginTop: '1rem', fontWeight: 600 }}>
                Secure Internal Notes (PCI/HIPAA) 
                <TooltipIcon text="Fully encrypted and HIPAA-compliant. To maintain PCI-DSS compliance, do not store full credit card numbers or CVV codes in these notes." isLight={isLight} />
              </label>
              <style dangerouslySetInnerHTML={{__html: `
                .custom-quill-container {
                  display: flex;
                  flex-direction: column;
                  resize: vertical;
                  overflow: auto;
                  min-height: 150px;
                }
                .custom-quill-container .quill {
                  display: flex;
                  flex-direction: column;
                  flex: 1;
                }
                .custom-quill-container .ql-container {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                }
                .custom-quill-container .ql-editor {
                  flex: 1;
                  font-family: inherit;
                  font-size: 0.95rem;
                  line-height: 1.6;
                  color: ${isLight ? '#000000' : '#ffffff'};
                }
                .custom-quill-container .ql-editor p,
                .custom-quill-container .ql-editor h1,
                .custom-quill-container .ql-editor h2,
                .custom-quill-container .ql-editor h3,
                .custom-quill-container .ql-editor h4,
                .custom-quill-container .ql-editor h5,
                .custom-quill-container .ql-editor h6,
                .custom-quill-container .ql-editor li {
                  color: ${isLight ? '#000000' : '#ffffff'};
                }
                .custom-quill-container .ql-editor p {
                  margin-bottom: 0.5em;
                }
                ${!isLight ? `
                .custom-quill-container .ql-toolbar .ql-stroke { stroke: #d4d4d8; }
                .custom-quill-container .ql-toolbar .ql-fill { fill: #d4d4d8; }
                .custom-quill-container .ql-toolbar .ql-picker { color: #d4d4d8; }
                .custom-quill-container .ql-snow .ql-picker.ql-expanded .ql-picker-options { background: #18181b; border-color: rgba(255,255,255,0.2); }
                ` : ''}
              `}} />
              <div className="custom-quill-container" style={{ background: isLight ? '#fff' : '#18181b', color: isLight ? '#09090b' : 'white', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)' }}>
                <ReactQuill theme="snow" value={secureNotes} onChange={setSecureNotes} />
              </div>
            </div>

            {keyFingerprint && (
              <div style={{ marginTop: '1rem', padding: '1rem', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)' }}>
                 <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: isLight ? '#09090b' : 'white' }}>Cryptography & Security</h4>
                 <div style={{ fontSize: '0.8rem', color: isLight ? '#4b5563' : '#a1a1aa', marginBottom: '1rem' }}>
                    <strong>Key Identifier Code:</strong> {keyFingerprint}<br/>
                    <strong>Last Updated:</strong> {keyUpdatedAt ? new Date(keyUpdatedAt * 1000).toLocaleString() : 'Unknown'}
                 </div>
                 
                 <div style={{ display: 'flex', gap: '0.5rem' }}>
                   <button type="button" onClick={async () => {
                      if(await confirm('Are you sure? This will generate a new AES key and re-encrypt the secure fields.')) {
                         setRegenerateClientKey(true);
                         showToast('Key flagged for rotation on save.');
                      }
                   }} style={{ padding: '0.5rem 1rem', background: 'transparent', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', color: isLight ? '#09090b' : 'white', cursor: 'pointer', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}
                   title="Security Maintenance: Generates a brand new personal encryption key and re-encrypts the data without losing it. Old stolen keys become useless.">
                     Rotate Key <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                   </button>

                   <button type="button" onClick={async () => {
                      const conf = await prompt('WARNING: This permanently deletes the encryption key and all secure fields. They cannot be recovered. Type PURGE to confirm.');
                      if(conf === 'PURGE') {
                         const newPass = generatePassphrase();
                         const newFields = [
                             { key: 'Password', value: newPass, secure: true, unmasked: true },
                             { key: 'Birthday', value: '', secure: true, unmasked: false }
                         ];
                         setSecureFields(newFields);
                         setSecureNotes('');
                         setRegenerateClientKey(true);
                         showToast('Secure data purged. New encryption key and password generated.', 'success');
                         // Auto-save the new password immediately
                         await handleSave(newFields, true);
                      }
                   }} style={{ padding: '0.5rem 1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '4px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}
                   title="Data Annihilation: Destroys the encryption key and secure fields permanently for regulatory compliance (e.g. Right to be Forgotten).">
                     Crypto-Shred <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                   </button>
                 </div>
                 
                 {(purgeSecureData || regenerateClientKey) && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#eab308' }}>
                       ⚠️ Changes will apply when you click Save.
                    </div>
                 )}
              </div>
            )}
            </>
            )}
          </div>
          
          {!isMergeMode && (
          <div style={{ padding: '1.5rem', borderTop: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '1rem', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)' }}>
            <button onClick={() => onClose(false)} disabled={loading} style={{ flex: 1, padding: '0.75rem', background: isLight ? '#e5e7eb' : 'rgba(255,255,255,0.1)', color: isLight ? '#09090b' : 'white', border: 'none', borderRadius: '6px', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => handleSave()} disabled={loading || !name || secureFields.some(f => f.key.toLowerCase() === 'password' && !isPasswordStrong(f.value))} style={{ flex: 1, padding: '0.75rem', background: '#38bdf8', color: '#09090b', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: (loading || !name || secureFields.some(f => f.key.toLowerCase() === 'password' && !isPasswordStrong(f.value))) ? 'not-allowed' : 'pointer', opacity: (!name || secureFields.some(f => f.key.toLowerCase() === 'password' && !isPasswordStrong(f.value))) ? 0.5 : 1 }}>{loading ? 'Saving...' : 'Save Contact'}</button>
          </div>
          )}
       </div>
    </div>
  );
}

function AddressBookSettingsModal({ open, onClose, isLight, lastKeyRotationAt, lastPasswordRotationAt, onRotatePasswords, onRotateKeys }: any) {
  if (!open) return null;

  return (
    <div onClick={() => onClose()} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '1rem', boxSizing: 'border-box' }}>
      <div onClick={e => e.stopPropagation()} style={{ position: 'relative', background: isLight ? '#ffffff' : '#18181b', borderRadius: '12px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)' }}>
          <h2 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1.25rem' }}>Address Book Settings</h2>
          <button onClick={() => onClose()} style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', padding: '0.5rem' }}>✕</button>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: isLight ? '#09090b' : 'white', fontSize: '1rem' }}>Encryption Keys</h3>
            <div style={{ color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.85rem', marginBottom: '1rem' }}>Rotate the AES-256 encryption keys for all contacts. This process is secure and will not affect user access.</div>
            <button onClick={() => { onClose(); onRotateKeys(); }} style={{ background: 'transparent', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', color: isLight ? '#09090b' : 'white', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Rotate All Keys</button>
            {lastKeyRotationAt && <div style={{ color: isLight ? '#9ca3af' : '#71717a', fontSize: '0.75rem', marginTop: '0.5rem' }}>Last rotated: {new Date(lastKeyRotationAt * 1000).toLocaleString()}</div>}
          </div>
          <div style={{ borderTop: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#ef4444', fontSize: '1rem' }}>Danger Zone: Passwords</h3>
            <div style={{ color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.85rem', marginBottom: '1rem' }}>Rotating passwords will immediately disconnect all users and require them to use new credentials.</div>
            <button onClick={() => { onClose(); onRotatePasswords(); }} style={{ background: 'transparent', border: isLight ? '1px solid #ef4444' : '1px solid rgba(239,68,68,0.5)', color: '#ef4444', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Rotate All Passwords</button>
            {lastPasswordRotationAt && <div style={{ color: isLight ? '#9ca3af' : '#71717a', fontSize: '0.75rem', marginTop: '0.5rem' }}>Last rotated: {new Date(lastPasswordRotationAt * 1000).toLocaleString()}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AddressBookManagerModal({ open, onClose, isLight, slug }: any) {
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useDialog();
  const [search, setSearch] = useState('');
  const [lastKeyRotationAt, setLastKeyRotationAt] = useState<number | null>(null);
  const [lastPasswordRotationAt, setLastPasswordRotationAt] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const [rotating, setRotating] = useState(false);
  const [rotateProgress, setRotateProgress] = useState(0);
  const [rotateTotal, setRotateTotal] = useState(0);
  
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<any>(null);

  const fetchContacts = async () => {
     setLoading(true);
     try {
        const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
        const res = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book`, {
           headers: { 'x-crm-password': pw }
        });
        if (res.ok) {
           const json = await res.json();
           setContacts(json.data || []);
           setLastKeyRotationAt(json.lastKeyRotationAt || null);
           setLastPasswordRotationAt(json.lastPasswordRotationAt || null);
        } else {
           const errJson = await res.json().catch(() => ({}));
           const err = new Error(errJson?.error || `HTTP ${res.status} failed to fetch contacts`);
           (err as any).status = res.status;
           throw err;
        }
     } catch (e: any) {
        console.error('[AddressBook Fetch Error]', e);
        reportDataFetchError('crm', 'AddressBook', e, { slug });
        if (e?.status !== 401 && e?.status !== 403) {
           showToast('Unable to load contacts at this time.', 'error');
        }
     } finally {
        setLoading(false);
     }
  };

  useEffect(() => {
     if (open) fetchContacts();
  }, [open]);

  if (!open) return null;

  const handleRotateAllKeys = async () => {
     if (contacts.length === 0) {
        showToast('No contacts to rotate keys for.', 'info');
        return;
     }
     if (!(await confirm(`Are you sure you want to rotate the encryption keys for all ${contacts.length} contacts? This cannot be undone.`))) return;
     
     setRotating(true);
     setRotateTotal(contacts.length);
     setRotateProgress(0);

     const chunkSize = 50;
     const contactIds = contacts.map(c => c.id);
     
     try {
         const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
         for (let i = 0; i < contactIds.length; i += chunkSize) {
             const chunk = contactIds.slice(i, i + chunkSize);
             const res = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book/rotate-keys`, {
                 method: 'POST',
                 headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
                 body: JSON.stringify({ contactIds: chunk })
             });
             
             if (!res.ok) {
                 const err = await res.json().catch(()=>({}));
                 throw new Error(err.error || 'Server error during key rotation');
             }
             
             setRotateProgress(Math.min(i + chunkSize, contactIds.length));
         }
         showToast('All encryption keys successfully rotated.', 'success');
         await fetchContacts();
     } catch (err: any) {
         showToast('Rotation failed: ' + err.message, 'error');
     }
     
     setRotating(false);
  };

  const handleRotateAllPasswords = async () => {
     if (contacts.length === 0) {
        showToast('No contacts to rotate passwords for.', 'info');
        return;
     }
     if (!(await confirm(`Are you sure you want to rotate the passwords for all ${contacts.length} contacts? This will lock everyone out until they are given their new passwords.`))) return;
     
     setRotating(true);
     setRotateTotal(contacts.length);
     setRotateProgress(0);

     const chunkSize = 50;
     const contactIds = contacts.map(c => c.id);
     
     try {
         const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
         for (let i = 0; i < contactIds.length; i += chunkSize) {
             const chunk = contactIds.slice(i, i + chunkSize);
             const res = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book/rotate-passwords`, {
                 method: 'POST',
                 headers: { 'Content-Type': 'application/json', 'x-crm-password': pw },
                 body: JSON.stringify({ contactIds: chunk })
             });
             
             if (!res.ok) {
                 const err = await res.json().catch(()=>({}));
                 throw new Error(err.error || 'Server error during password rotation');
             }
             
             setRotateProgress(Math.min(i + chunkSize, contactIds.length));
         }
         showToast('All passwords successfully rotated.', 'success');
         await fetchContacts();
     } catch (err: any) {
         showToast('Rotation failed: ' + err.message, 'error');
     }
     
     setRotating(false);
  };

  const handleDelete = async (contactId: string, name: string) => {
     if (!(await confirm(`Permanently delete ${name}?`))) return;
     setLoading(true);
     try {
        const pw = localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '';
        const res = await crmFetch(`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/address-book/${contactId}`, {
           method: 'DELETE',
           headers: { 'x-crm-password': pw }
        });
        if (res.ok) {
           await fetchContacts();
        } else {
           const err = await res.json(); showToast('Failed to delete: ' + (err.error || 'Unknown error'), 'error');
        }
     } catch (err) {}
     setLoading(false);
  };

  const filteredContacts = contacts.filter(c => {
      const q = search.toLowerCase();
      const numQ = q.replace(/\D/g, '');
      const safeName = c.name?.toLowerCase() || '';
      const safeCompany = c.company?.toLowerCase() || '';
      const numMatch = c.numbers?.some((n: any) => {
          if (!n.number) return false;
          const cleanN = String(n.number).replace(/\D/g, '');
          return n.number.toLowerCase().includes(q) || (numQ.length >= 3 && cleanN.includes(numQ));
      });
      return safeName.includes(q) || safeCompany.includes(q) || numMatch;
  });

  return (
    <>
    <div onClick={() => onClose(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9990, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '1rem', boxSizing: 'border-box' }}>
       <div onClick={e => e.stopPropagation()} style={{ position: 'relative', background: isLight ? '#ffffff' : '#18181b', borderRadius: '12px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', width: '100%', maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', overflow: 'hidden' }}>
          
          {rotating && (
             <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: isLight ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                <div style={{ background: isLight ? '#fff' : '#18181b', padding: '2rem', borderRadius: '12px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', width: '400px', textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: isLight ? '#09090b' : 'white' }}>Rotating Keys...</h3>
                    <div style={{ width: '100%', background: isLight ? '#e5e7eb' : '#27272a', borderRadius: '999px', height: '8px', overflow: 'hidden', marginBottom: '1rem' }}>
                        <div style={{ width: `${(rotateTotal > 0 ? (rotateProgress / rotateTotal) : 0) * 100}%`, background: '#38bdf8', height: '100%', transition: 'width 0.3s ease' }}></div>
                    </div>
                    <div style={{ color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.9rem' }}>Processed {rotateProgress} of {rotateTotal} contacts.</div>
                    <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '1rem', fontWeight: 500 }}>Please do not close this window.</div>
                </div>
             </div>
          )}
          <div style={{ padding: '1.5rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)' }}>
             <div>
                <h2 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1.25rem' }}>Address Book</h2>
                <div style={{ color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.85rem', marginTop: '0.25rem' }}>Manage contacts and secure information for this PBX.</div>
             </div>
             <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button onClick={() => setSettingsOpen(true)} style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Settings">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                </button>
                <button onClick={() => onClose(false)} style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Close">✕</button>
             </div>
          </div>

          <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
             <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
                 <input 
                     type="text" 
                     placeholder="Search contacts..." 
                     value={search} 
                     onChange={e => setSearch(e.target.value)}
                     style={{ padding: '0.75rem', borderRadius: '6px', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', background: isLight ? '#ffffff' : 'rgba(0,0,0,0.3)', color: isLight ? '#09090b' : 'white', width: '100%', maxWidth: '300px', boxSizing: 'border-box' }}
                 />
                 <button onClick={() => { setEditingContact(null); setEditorOpen(true); }} style={{ background: '#38bdf8', border: 'none', color: '#09090b', padding: '0.75rem 1.5rem', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>+ New Contact</button>
             </div>

             {loading && contacts.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#6b7280' : '#a1a1aa' }}>Loading contacts...</div>
             ) : contacts.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '3rem 2rem', color: isLight ? '#6b7280' : '#a1a1aa', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>No contacts found in the address book.</div>
             ) : filteredContacts.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '3rem 2rem', color: isLight ? '#6b7280' : '#a1a1aa', background: isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>No contacts match your search.</div>
             ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', tableLayout: 'fixed' }}>
                   <thead>
                      <tr style={{ textAlign: 'left', borderBottom: isLight ? '2px solid #e5e7eb' : '2px solid rgba(255,255,255,0.1)' }}>
                         <th style={{ padding: '0.75rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Name</th>
                         <th style={{ padding: '0.75rem', color: isLight ? '#4b5563' : '#a1a1aa', width: '30%' }}>Company</th>
                         <th style={{ padding: '0.75rem', color: isLight ? '#4b5563' : '#a1a1aa', width: '25%' }}>Primary #</th>
                         <th style={{ padding: '0.75rem', textAlign: 'right', color: isLight ? '#4b5563' : '#a1a1aa', width: '80px' }}>Actions</th>
                      </tr>
                   </thead>
                   <tbody>
                      {filteredContacts.map(c => {
                         const primaryNum = c.numbers?.find((n: any) => n.isPrimary) || c.numbers?.[0];
                         return (
                            <tr key={c.id} style={{ borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.05)' }}>
                               <td style={{ padding: '0.75rem', color: isLight ? '#09090b' : 'white', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</td>
                               <td style={{ padding: '0.75rem', color: isLight ? '#6b7280' : '#a1a1aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.company || '-'}</td>
                               <td style={{ padding: '0.75rem', color: isLight ? '#6b7280' : '#a1a1aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primaryNum ? primaryNum.number : '-'}</td>
                               <td style={{ padding: '0.75rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
                                     <button onClick={() => { setEditingContact(c); setEditorOpen(true); }} title="Edit Contact" style={{ background: 'transparent', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity='1'} onMouseLeave={e => e.currentTarget.style.opacity='0.7'}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                     </button>
                                     <button onClick={() => handleDelete(c.id, c.name)} title="Delete Contact" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={e => e.currentTarget.style.opacity='1'} onMouseLeave={e => e.currentTarget.style.opacity='0.7'}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                     </button>
                                  </div>
                               </td>
                            </tr>
                         );
                      })}
                   </tbody>
                </table>
             )}
          </div>
       </div>
    </div>
    
    <AddressBookSettingsModal 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)} 
        isLight={isLight} 
        lastKeyRotationAt={lastKeyRotationAt}
        lastPasswordRotationAt={lastPasswordRotationAt}
        onRotateKeys={handleRotateAllKeys}
        onRotatePasswords={handleRotateAllPasswords}
    />
    
    {editorOpen && (
       <ContactEditorModal 
          open={editorOpen} 
          onClose={(refresh: boolean) => { setEditorOpen(false); if (refresh) fetchContacts(); }}
          contactId={editingContact?.id}
          defaultData={editingContact}
          isLight={isLight}
          slug={slug}
       />
    )}
    </>
  );
}
